import React, { useState, useContext } from "react";
import { Auth, API, graphqlOperation } from "aws-amplify";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import UserContext from "./UserContext";
import { createUser } from "../graphql/mutations";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const ACCENT_SOFT = "rgba(56, 84, 232, 0.12)";
const SURFACE = "#ffffff";
const CANVAS = "#f6f6f4";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontFamily: SANS,
    fontSize: "1rem",
    fontWeight: 500,
    borderRadius: "12px",
    backgroundColor: SURFACE,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    boxShadow: "inset 0 1px 0 rgba(15, 17, 33, 0.02)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.14)",
      transition: "border-color 0.2s ease"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.32)"
    },
    "&.Mui-focused": {
      boxShadow:
        "0 0 0 3px rgba(56, 84, 232, 0.14), inset 0 1px 0 rgba(15, 17, 33, 0.02)"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ACCENT,
      borderWidth: "1px"
    }
  },
  "& .MuiInputLabel-outlined": {
    fontFamily: SANS,
    fontSize: "0.95rem",
    color: INK_MUTED,
    "&.Mui-focused": { color: ACCENT }
  }
};

const primaryButtonSx = {
  fontSize: "0.95rem",
  fontWeight: 600,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3,
  py: 1.3,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" },
  "&.Mui-disabled": {
    backgroundColor: "rgba(15, 17, 33, 0.18)",
    color: "rgba(255, 255, 255, 0.85)",
    boxShadow: "none"
  }
};

const ghostButtonSx = {
  fontSize: "0.94rem",
  fontWeight: 500,
  fontFamily: SANS,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2.25,
  py: 1.25,
  borderRadius: "12px",
  color: INK,
  backgroundColor: SURFACE,
  border: `1px solid ${INK_HAIRLINE}`,
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: "rgba(15, 17, 33, 0.32)",
    backgroundColor: SURFACE,
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const dialogPaperSx = {
  borderRadius: "18px",
  border: `1px solid ${INK_HAIRLINE}`,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -28px rgba(15, 17, 33, 0.22)",
  backgroundColor: SURFACE
};

const GoogleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ flexShrink: 0, display: "block" }}
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC04"
      d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

export default function LogIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { setUser } = useContext(UserContext);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [usernameExistsDialogOpen, setUsernameExistsDialogOpen] = useState(
    false
  );
  const [signUpError, setSignUpError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [codeMismatchDialogOpen, setCodeMismatchDialogOpen] = useState(false);

  const toggleSignUp = () => setIsSignUp(!isSignUp);
  const navigate = useNavigate();

  async function signUp() {
    if (username && password && email && name) {
      const lowerCaseUsername = username.toLowerCase();
      try {
        const { user } = await Auth.signUp({
          username: lowerCaseUsername,
          password,
          attributes: { email, name },
          autoSignIn: { enabled: true }
        });
        setUser(user);
        setConfirmationDialogOpen(true);
      } catch (error) {
        if (error.code === "UsernameExistsException") {
          setUsernameExistsDialogOpen(true);
        } else {
          console.log("error signing up:", error);
          setSignUpError(true);
        }
      }
    }
  }

  async function resendConfirmationCode() {
    try {
      await Auth.resendSignUp(username);
    } catch (err) {
      console.log("error resending code: ", err);
    }
  }

  async function confirmSignUp() {
    try {
      setLoading(true);
      const lowerCaseUsername = username.toLowerCase();
      await Auth.confirmSignUp(lowerCaseUsername, confirmationCode);
      await Auth.signIn(lowerCaseUsername, password).then(user =>
        setUser(user)
      );
      await API.graphql(
        graphqlOperation(createUser, {
          input: {
            id: lowerCaseUsername,
            username: lowerCaseUsername,
            name,
            dailyQueries: 0,
            subscriptionType: "FREE"
          }
        })
      ).then(() => {
        setConfirmationDialogOpen(false);
        navigate("/profile");
      });
    } catch (error) {
      if (error.code === "CodeMismatchException") {
        setCodeMismatchDialogOpen(true);
      }
      console.log("error confirming sign up", error.code);
    } finally {
      setLoading(false);
    }
  }

  async function signIn() {
    try {
      setLoadingSignIn(true);
      const lowerCaseUsername = username.toLowerCase();
      const user = await Auth.signIn(lowerCaseUsername, password);
      setUser(user);
      navigate("/profile");
    } catch (error) {
      console.log("error signing in", error);
    } finally {
      setLoadingSignIn(false);
    }
  }

  async function handleSocialSignIn() {
    await Auth.federatedSignIn({ provider: "Google" });
  }

  const canSubmit = isSignUp
    ? username && password && email && name
    : username && password;

  const onSubmit = e => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isSignUp) signUp();
    else signIn();
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: CANVAS,
        minHeight: "100vh",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3 },
        textAlign: "left",
        overflow: "hidden"
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 45% at 80% 0%, rgba(228, 222, 207, 0.5), transparent 65%), radial-gradient(ellipse 50% 40% at 12% 8%, rgba(56, 84, 232, 0.06), transparent 72%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0) 65%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0) 65%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />

      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 110, damping: 22 }}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 6fr) minmax(0, 5fr)"
            },
            gap: { xs: 4, md: 8 },
            alignItems: "center",
            minHeight: { md: "calc(100vh - 200px)" }
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5
              }}
            >
              <Box
                component={motion.span}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ACCENT
                }}
              />
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_MUTED
                }}
              >
                {isSignUp ? "Create account" : "Sign in"}
              </Typography>
            </Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.2rem" },
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                color: INK,
                m: 0,
                mb: 2.5,
                maxWidth: "16ch"
              }}
            >
              {isSignUp ? "Make an account." : "Welcome back."}
            </Typography>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: { xs: "1rem", md: "1.08rem" },
                lineHeight: 1.55,
                color: INK_SOFT,
                m: 0,
                mb: 4,
                maxWidth: "44ch"
              }}
            >
              {isSignUp
                ? "Free to start. No card needed for the first ten feedback loops a day."
                : "Pick up where you left off. Saved questions and feedback are waiting."}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap"
              }}
            >
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.92rem",
                  color: INK_FAINT
                }}
              >
                {isSignUp ? "Already have an account?" : "New here?"}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={toggleSignUp}
                sx={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: SANS,
                  fontSize: "0.92rem",
                  fontWeight: 500,
                  color: ACCENT,
                  p: 0,
                  textDecoration: "underline",
                  textDecorationColor: ACCENT_SOFT,
                  textUnderlineOffset: 3,
                  transition: "text-decoration-color 0.15s ease",
                  "&:hover": { textDecorationColor: ACCENT }
                }}
              >
                {isSignUp ? "Sign in" : "Create an account"}
              </Box>
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={onSubmit}
            noValidate
            autoComplete="off"
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${INK_HAIRLINE}`,
              borderRadius: "20px",
              p: { xs: 3, md: 4 },
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -28px rgba(15, 17, 33, 0.22), 0 6px 14px -6px rgba(15, 17, 33, 0.08)"
            }}
          >
            <Button
              type="button"
              onClick={handleSocialSignIn}
              disableRipple
              sx={{
                ...ghostButtonSx,
                width: "100%",
                gap: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <GoogleIcon />
              Continue with Google
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                my: 3
              }}
            >
              <Box
                sx={{ flex: 1, height: "1px", backgroundColor: INK_HAIRLINE }}
              />
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_FAINT
                }}
              >
                or with email
              </Typography>
              <Box
                sx={{ flex: 1, height: "1px", backgroundColor: INK_HAIRLINE }}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
              <TextField
                required
                label="Username"
                variant="outlined"
                value={username}
                onChange={e => setUsername(e.target.value)}
                sx={inputSx}
              />
              <TextField
                required
                label="Password"
                type="password"
                variant="outlined"
                value={password}
                onChange={e => setPassword(e.target.value)}
                sx={inputSx}
              />
              {isSignUp && (
                <>
                  <TextField
                    required
                    label="Name"
                    variant="outlined"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    sx={inputSx}
                  />
                  <TextField
                    required
                    label="Email"
                    type="email"
                    variant="outlined"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    sx={inputSx}
                  />
                </>
              )}
            </Box>

            <Button
              type="submit"
              disabled={!canSubmit || loading || loadingSignIn}
              disableRipple
              endIcon={
                !loading && !loadingSignIn ? (
                  <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                ) : undefined
              }
              sx={{ ...primaryButtonSx, width: "100%", mt: 2.5 }}
            >
              {loadingSignIn || loading ? (
                <CircularProgress size={18} sx={{ color: "#fafaf7" }} />
              ) : isSignUp ? (
                "Create account"
              ) : (
                "Sign in"
              )}
            </Button>

            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                color: INK_FAINT,
                mt: 3,
                pt: 2.5,
                borderTop: `1px solid ${INK_HAIRLINE_SOFT}`
              }}
            >
              By {isSignUp ? "creating an account" : "signing in"} you agree to
              the{" "}
              <Box
                component={RouterLink}
                to="/terms"
                sx={{
                  color: INK_SOFT,
                  textDecoration: "underline",
                  textDecorationColor: INK_HAIRLINE,
                  textUnderlineOffset: 2,
                  transition: "color 0.15s ease, text-decoration-color 0.15s ease",
                  "&:hover": {
                    color: ACCENT,
                    textDecorationColor: ACCENT_SOFT
                  }
                }}
              >
                Terms
              </Box>{" "}
              and{" "}
              <Box
                component={RouterLink}
                to="/privacy"
                sx={{
                  color: INK_SOFT,
                  textDecoration: "underline",
                  textDecorationColor: INK_HAIRLINE,
                  textUnderlineOffset: 2,
                  transition: "color 0.15s ease, text-decoration-color 0.15s ease",
                  "&:hover": {
                    color: ACCENT,
                    textDecorationColor: ACCENT_SOFT
                  }
                }}
              >
                Privacy Policy
              </Box>
              .
            </Typography>
          </Box>
        </Box>
      </Container>

      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em",
            pb: 1
          }}
        >
          Confirm your email
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55,
              mb: 1.5
            }}
          >
            We sent a code to your email. Enter it below to finish creating
            your account.
          </DialogContentText>
          <TextField
            autoFocus
            label="Confirmation code"
            variant="outlined"
            fullWidth
            value={confirmationCode}
            onChange={e => setConfirmationCode(e.target.value)}
            sx={inputSx}
          />
          <Box sx={{ mt: 1.5 }}>
            <Box
              component="button"
              type="button"
              onClick={resendConfirmationCode}
              sx={{
                appearance: "none",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontFamily: SANS,
                fontSize: "0.85rem",
                fontWeight: 500,
                color: ACCENT,
                p: 0,
                "&:hover": { textDecoration: "underline" }
              }}
            >
              Resend code
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmationDialogOpen(false)}
            disableRipple
            sx={ghostButtonSx}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirmSignUp}
            disableRipple
            sx={primaryButtonSx}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={usernameExistsDialogOpen}
        onClose={() => setUsernameExistsDialogOpen(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em"
          }}
        >
          That username is taken
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55
            }}
          >
            Try another one. Usernames are case-insensitive.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setUsernameExistsDialogOpen(false)}
            disableRipple
            sx={primaryButtonSx}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={signUpError}
        onClose={() => setSignUpError(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em"
          }}
        >
          Couldn't create that account
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55
            }}
          >
            Double-check your details and try again. Passwords need a mix of
            cases, a number, and at least eight characters.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setSignUpError(false)}
            disableRipple
            sx={primaryButtonSx}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={codeMismatchDialogOpen}
        onClose={() => setCodeMismatchDialogOpen(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: "1.2rem",
            color: INK,
            letterSpacing: "-0.01em"
          }}
        >
          Wrong code
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              color: INK_SOFT,
              lineHeight: 1.55
            }}
          >
            That confirmation code didn't match. Try again or resend a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCodeMismatchDialogOpen(false)}
            disableRipple
            sx={primaryButtonSx}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={loadingSignIn || loading}
        sx={{
          zIndex: theme => theme.zIndex.drawer + 1,
          color: INK,
          backgroundColor: "rgba(246, 246, 244, 0.65)",
          backdropFilter: "blur(4px)"
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Backdrop>
    </Box>
  );
}
